import { DocumentType, StatusScene, StatusTone } from "./types";

/** Status novog dokumenta, dok još nije spremljen. */
export const DOCUMENT_DRAFT_STATUS = "draft";

/** Klase po boji: pečat na dokumentu, kvadratić ikone i boja ikone. */
export const STATUS_TONE_CLASSES: Record<StatusTone, { badge: string; iconBox: string; icon: string }> = {
  slate: { badge: "bg-slate-100 text-slate-600", iconBox: "bg-slate-100", icon: "text-slate-600" },
  blue: { badge: "bg-blue-50 text-blue-700", iconBox: "bg-blue-100", icon: "text-blue-700" },
  sky: { badge: "bg-sky-50 text-sky-700", iconBox: "bg-sky-100", icon: "text-sky-700" },
  amber: { badge: "bg-amber-50 text-amber-600", iconBox: "bg-amber-100", icon: "text-amber-600" },
  emerald: { badge: "bg-emerald-50 text-emerald-600", iconBox: "bg-emerald-100", icon: "text-emerald-600" },
  violet: { badge: "bg-violet-50 text-violet-600", iconBox: "bg-violet-100", icon: "text-violet-600" },
  indigo: { badge: "bg-indigo-50 text-indigo-600", iconBox: "bg-indigo-100", icon: "text-indigo-600" },
  rose: { badge: "bg-rose-50 text-rose-600", iconBox: "bg-rose-100", icon: "text-rose-600" },
};

/** Naziv i broj dokumenta na velikoj kartici, za prodaju i najam. */
export const DOCUMENT_LABELS: Record<DocumentType, { sale: string; rent: string; number: string }> = {
  offer: { sale: "Ponuda", rent: "Ponuda za najam", number: "PON-2026-0042" },
  contract: { sale: "Kupoprodajni ugovor", rent: "Ugovor o najmu", number: "UG-2026-0017" },
  invoice: { sale: "Račun", rent: "Račun za najam", number: "RAČ-2026-0118" },
};

/** Primjer iznosa: prodaja ukupno, najam po danu. */
export const DOCUMENT_SAMPLE_AMOUNTS = {
  sale: { label: "Ukupno", value: "12.450,00 €" },
  rent: { label: "7 dana × 65,00 €", value: "455,00 €" },
};

/** Scena za svaki status svake vrste dokumenta. */
export const DOCUMENT_STATUS_SCENES: Record<DocumentType, Record<string, StatusScene>> = {
  offer: {
    draft: {
      badge: "Predložak",
      tone: "slate",
      title: "Ponuda u pripremi",
      description: "Dodajte kupca i stavke. Iznosi i porez se računaju sami.",
      variant: "items",
      notice: { title: "Cijena iz vozila", subtitle: "Stavka vozila je već dodana", icon: "edit", tone: "blue" },
    },
    created: {
      badge: "Kreirana",
      tone: "blue",
      title: "Ponuda je kreirana",
      description: "PDF je spreman. Pregledajte ga i pošaljite kupcu.",
      variant: "pdf",
      notice: { title: "Sljedeći korak", subtitle: "Pošaljite ponudu emailom", icon: "mail", tone: "sky" },
    },
    sent: {
      badge: "Poslana",
      tone: "sky",
      title: "Ponuda je poslana",
      description: "Kupac je dobio ponudu emailom. Čekamo njegov odgovor.",
      variant: "email",
      notice: { title: "Kupac je otvorio email", subtitle: "Danas u 10:42", icon: "check", tone: "emerald" },
    },
    accepted: {
      badge: "Prihvaćena",
      tone: "amber",
      title: "Ponuda je prihvaćena",
      description: "Kupac je prihvatio ponudu. Iz nje napravite ugovor jednim klikom.",
      variant: "approved",
      notice: { title: "Sljedeći korak", subtitle: "Pretvorite ponudu u ugovor", icon: "file", tone: "blue" },
    },
    paid: {
      badge: "Plaćena",
      tone: "emerald",
      title: "Ponuda je plaćena",
      description: "Uplata je evidentirana i ponuda je zatvorena.",
      variant: "payment",
      notice: { title: "Uplata zaprimljena", subtitle: "Iznos je knjižen na ponudu", icon: "euro", tone: "emerald" },
    },
    cancelled: {
      badge: "Stornirana",
      tone: "rose",
      title: "Ponuda je stornirana",
      description: "Ponuda više ne vrijedi, ali ostaje sačuvana u povijesti.",
      variant: "cancelled",
      notice: { title: "Vozilo je ponovno slobodno", subtitle: "Možete napraviti novu ponudu", icon: "x", tone: "rose" },
    },
  },
  contract: {
    draft: {
      badge: "Predložak",
      tone: "slate",
      title: "Ugovor u pripremi",
      description: "Podaci kupca i vozila preuzimaju se iz ponude. Provjerite ih i dopunite.",
      variant: "items",
      notice: { title: "Podaci iz ponude", subtitle: "Kupac, vozilo i cijena su preneseni", icon: "edit", tone: "blue" },
    },
    created: {
      badge: "Kreiran",
      tone: "blue",
      title: "Ugovor je kreiran",
      description: "PDF ugovora je spreman za pregled i slanje kupcu.",
      variant: "pdf",
      notice: { title: "Sljedeći korak", subtitle: "Pošaljite ugovor na potpis", icon: "mail", tone: "sky" },
    },
    sent: {
      badge: "Poslan",
      tone: "sky",
      title: "Ugovor je poslan",
      description: "Kupac je dobio ugovor emailom i može ga potpisati.",
      variant: "email",
      notice: { title: "Čeka potpis kupca", subtitle: "Obavijestit ćemo vas", icon: "check", tone: "amber" },
    },
    signed: {
      badge: "Potpisan",
      tone: "emerald",
      title: "Ugovor je potpisan",
      description: "Obje strane su potpisale ugovor. Možete izdati račun.",
      variant: "signature",
      notice: { title: "Sljedeći korak", subtitle: "Izdajte račun iz ugovora", icon: "receipt", tone: "blue" },
    },
    invoiced: {
      badge: "Fakturiran",
      tone: "violet",
      title: "Ugovor je fakturiran",
      description: "Račun je izdan iz ugovora i povezan s njim.",
      variant: "invoiceLink",
      notice: { title: "Račun je povezan", subtitle: "Vidljiv je i na detaljima vozila", icon: "receipt", tone: "violet" },
    },
    cancelled: {
      badge: "Storniran",
      tone: "rose",
      title: "Ugovor je storniran",
      description: "Ugovor više ne vrijedi, ali ostaje sačuvan u povijesti.",
      variant: "cancelled",
      notice: { title: "Vozilo je ponovno slobodno", subtitle: "Možete napraviti novi ugovor", icon: "x", tone: "rose" },
    },
  },
  invoice: {
    draft: {
      badge: "Predložak",
      tone: "slate",
      title: "Račun u pripremi",
      description: "Stavke se preuzimaju iz ugovora. Provjerite iznose i način plaćanja.",
      variant: "items",
      notice: { title: "Stavke iz ugovora", subtitle: "Iznosi i PDV su preneseni", icon: "edit", tone: "blue" },
    },
    created: {
      badge: "Kreiran",
      tone: "blue",
      title: "Račun je kreiran",
      description: "Račun je spreman. Fiskalizirajte ga prije slanja kupcu.",
      variant: "pdf",
      notice: { title: "Sljedeći korak", subtitle: "Fiskalizirajte račun", icon: "shield", tone: "indigo" },
    },
    fiscalized: {
      badge: "Fiskaliziran",
      tone: "indigo",
      title: "Račun je fiskaliziran",
      description: "Porezna je potvrdila račun i dodijelila mu JIR.",
      variant: "fiscal",
      notice: { title: "Sljedeći korak", subtitle: "Pošaljite račun kupcu", icon: "mail", tone: "sky" },
    },
    sent: {
      badge: "Poslan",
      tone: "sky",
      title: "Račun je poslan",
      description: "Kupac je dobio račun emailom ili kao e-račun.",
      variant: "email",
      notice: { title: "Čeka uplatu", subtitle: "Rok plaćanja 15 dana", icon: "euro", tone: "amber" },
    },
    paid: {
      badge: "Plaćen",
      tone: "emerald",
      title: "Račun je plaćen",
      description: "Uplata je evidentirana i račun je zatvoren.",
      variant: "payment",
      notice: { title: "Uplata zaprimljena", subtitle: "Račun je zatvoren", icon: "check", tone: "emerald" },
    },
    cancelled: {
      badge: "Storniran",
      tone: "rose",
      title: "Račun je storniran",
      description: "Izdan je storno račun, a izvorni ostaje sačuvan u povijesti.",
      variant: "cancelled",
      notice: { title: "Storno je fiskaliziran", subtitle: "Povezan s izvornim računom", icon: "x", tone: "rose" },
    },
  },
};
